import { useEffect, useRef } from 'react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import type { TypesenseChunkRecord } from '@/services/api/lake'

interface PageChunkListProps {
  chunks: TypesenseChunkRecord[]
  selectedChunkIds: Set<string>
  onToggleChunk: (id: string) => void
  /** If set, this chunk will be highlighted and auto-scrolled into view */
  highlightChunkId?: string
}

export function PageChunkList({
  chunks,
  selectedChunkIds,
  onToggleChunk,
  highlightChunkId,
}: PageChunkListProps) {
  const highlightRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll highlighted chunk into view when it changes
  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [])

  if (chunks.length === 0) {
    return <div className="text-xs text-muted-foreground italic py-2">No chunks on this page</div>
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground pb-1 border-b border-border">
        Chunks on this page ({chunks.length})
      </div>
      {chunks.map(chunk => {
        const isSelected = selectedChunkIds.has(chunk.id)
        const isHighlighted = chunk.id === highlightChunkId
        return (
          <div
            key={chunk.id}
            ref={isHighlighted ? highlightRef : null}
            className={`
              rounded-lg border p-3 transition-colors
              ${
                isHighlighted
                  ? 'border-info/60 bg-info/10'
                  : isSelected
                    ? 'border-primary/40 bg-muted/50'
                    : 'border-border bg-muted/20 hover:bg-muted/40'
              }
            `}
          >
            <div className="flex items-start gap-2.5">
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleChunk(chunk.id)}
                className="mt-0.5 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                {/* Metadata badges */}
                <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px] h-4">
                    #{chunk.chunk_index}
                  </Badge>
                  {chunk.chunk_type && (
                    <Badge variant="outline" className="text-[10px] h-4 text-primary">
                      {chunk.chunk_type}
                    </Badge>
                  )}
                  {chunk.section && (
                    <span className="text-[10px] text-muted-foreground truncate">
                      {chunk.section}
                    </span>
                  )}
                  {chunk.page_number !== null &&
                    chunk.page_number_end !== null &&
                    chunk.page_number_end !== chunk.page_number && (
                      <Badge variant="outline" className="text-[10px] h-4 text-amber-400">
                        pp.{chunk.page_number}–{chunk.page_number_end}
                      </Badge>
                    )}
                </div>
                {/* Full chunk content — always visible, no collapse */}
                <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap">
                  {chunk.content}
                </p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
